import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Dimensions,
  Animated,
  LayoutAnimation,
  UIManager,
  Image,
} from "react-native";
import { StatusBar } from "expo-status-bar";
import { Ionicons } from "@expo/vector-icons";
import AuthButton from "./auth/AuthButton";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigation";
import { useTheme } from "../contexts/ThemeContext";

if (
  Platform.OS === "android" &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

interface HeaderProps {
  onInfoPress?: () => void;
  onAdminPress?: () => void;
  transparent?: boolean;
  scrollY?: Animated.Value;
  backgroundColor?: string;
}

const { width } = Dimensions.get("window");
const isMobile = width < 768;

const Header: React.FC<HeaderProps> = ({ onInfoPress, onAdminPress, transparent = false, scrollY, backgroundColor }) => {
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { currentUser } = useAuth();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();

  const userType = currentUser?.get("userType");
  const isAdmin = !!userType && ["admin", "super_admin"].includes(userType);
  const isSchoolAdmin = userType === 'school_admin';
  const isTeacher = userType === 'teacher';

  const logoOpacity = React.useRef(new Animated.Value(0)).current;
  const logoTranslateY = React.useRef(new Animated.Value(-20)).current;
  const themeIconRotation = React.useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(logoOpacity, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.spring(logoTranslateY, {
        toValue: 0,
        friction: 8,
        tension: 40,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  // Animate theme icon when theme changes
  useEffect(() => {
    Animated.sequence([
      Animated.timing(themeIconRotation, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(themeIconRotation, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }),
    ]).start();
  }, [theme]);

  const toggleMobileMenu = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navigateToHome = () => {
    navigation.navigate("Home", {});
    if (mobileMenuOpen) setMobileMenuOpen(false);
  };

  const navigateToAbout = () => {
    if (onInfoPress) {
      onInfoPress();
    } else {
      navigation.navigate("About");
    }
    if (mobileMenuOpen) setMobileMenuOpen(false);
  };

  const navigateToContact = () => {
    navigation.navigate("Contact");
    if (mobileMenuOpen) setMobileMenuOpen(false);
  };

  const navigateToTeam = () => {
    navigation.navigate("Team");
    if (mobileMenuOpen) setMobileMenuOpen(false);
  };

  const navigateToSchoolAdminDashboard = () => {
    navigation.navigate("SchoolAdminDashboard");
    if (mobileMenuOpen) setMobileMenuOpen(false);
  };

  const navigateToTeacherPortal = () => {
    navigation.navigate("TeacherPortal");
    if (mobileMenuOpen) setMobileMenuOpen(false);
  };

  const isRouteActive = (routeName: string) => {
    return route.name === routeName;
  };

  // react-native-web renders TouchableOpacity as a <div>, so the nav produced no
  // crawlable links — /about and /contact were reachable only via the sitemap.
  // Marking the label as an anchor gives crawlers a real <a href>.
  //
  // The label must navigate itself: react-native-web's Text onPress consumes the
  // click rather than letting it bubble to the enclosing TouchableOpacity, so
  // handling only preventDefault here would leave the link inert. preventDefault
  // still matters — without it the browser does a full page load. Modifier and
  // middle clicks never reach onPress, so "open in new tab" keeps working.
  const navLinkProps = (path: string, navigate: () => void) =>
    Platform.OS === "web"
      ? ({
          href: path,
          accessibilityRole: "link",
          onPress: (e: any) => {
            e?.preventDefault?.();
            navigate();
          },
        } as any)
      : {};

  // Theme-aware colors
  const defaultBgColor = theme === "dark" ? "#18181B" : "#FFFFFF";
  const solidBgColor = backgroundColor || defaultBgColor;
  const solidBorderColor = theme === "dark" ? "#27272A" : "#E4E4E7";

  // Interpolate colors if scrollY is provided
  // Background should always be solid (unless transparent prop is set)
  // When transparent is true, interpolate from transparent to solid on scroll
  const animatedBgColor = transparent && scrollY
    ? scrollY.interpolate({
      inputRange: [0, 20],
      outputRange: ["rgba(0,0,0,0)", solidBgColor],
      extrapolate: "clamp"
    })
    : transparent
      ? "transparent"
      : solidBgColor;

  const animatedBorderColor = scrollY ? scrollY.interpolate({
    inputRange: [0, 20],
    outputRange: ["rgba(0,0,0,0)", solidBorderColor],
    extrapolate: "clamp"
  }) : (transparent ? "transparent" : solidBorderColor);

  const animatedShadowOpacity = scrollY ? scrollY.interpolate({
    inputRange: [0, 20],
    outputRange: [0, 0.05],
    extrapolate: "clamp"
  }) : 0.05;

  const animatedElevation = scrollY ? scrollY.interpolate({
    inputRange: [0, 20],
    outputRange: [0, 3],
    extrapolate: "clamp"
  }) : 3;

  const navText = theme === "dark" ? "#C2C2C2" : "#555555";
  const navActive = theme === "dark" ? "#FAFAFA" : "#272727";
  const iconColor = theme === "dark" ? "#C2C2C2" : "#555555";

  return (
    <Animated.View
      style={[
        styles.container,
        scrolled && styles.scrolledContainer,
        {
          backgroundColor: animatedBgColor,
          borderBottomColor: animatedBorderColor,
          shadowOpacity: animatedShadowOpacity,
          elevation: animatedElevation,
        },
      ]}
      data-oid="phd2rv4"
    >
      <StatusBar
        style={theme === "dark" ? "light" : "dark"}
        data-oid="grpdgwq"
      />

      <View style={styles.headerContent} data-oid="qk3l.gl">
        <Animated.View
          style={[
            styles.logoContainer,
            {
              opacity: logoOpacity,
              transform: [{ translateY: logoTranslateY }],
            },
          ]}
          data-oid="i_7z6d0"
        >
          <TouchableOpacity
            style={styles.logoTouchable}
            onPress={navigateToHome}
            data-oid="cvur5tn"
          >
            <View
              style={[
                styles.logoIconContainer,
                { backgroundColor: theme === "dark" ? "#232327" : "#F5F5F5" },
              ]}
              data-oid="5-j-rt1"
            >
              <Ionicons
                name="flask"
                size={24}
                color={navActive}
                data-oid="1ikkqf4"
              />
            </View>
            <View data-oid="x4l:vy9">
              <Text
                style={[styles.logoText, { color: navActive }]}
                data-oid="l_-9vnc"
              >
                BC STEM
              </Text>
              <Text
                style={[styles.opportunities, { color: navText }]}
                data-oid="40sc6v2"
              >
                Opportunities
              </Text>
            </View>
          </TouchableOpacity>

          {/* Canada Day Flag */}
          <Animated.View
            style={[
              styles.canadaFlagContainer,
              {
                opacity: logoOpacity,
                transform: [{ translateY: logoTranslateY }],
              }
            ]}
          >
            <Image
              // eslint-disable-next-line @typescript-eslint/no-require-imports
              source={require("../../assets/canada.png")}
              style={styles.canadaFlag}
              resizeMode="contain"
            />
          </Animated.View>
        </Animated.View>

        {isMobile ? (
          <>
            <TouchableOpacity
              style={styles.mobileMenuButton}
              onPress={toggleMobileMenu}
              activeOpacity={0.7}
              data-oid="-6-0m9e"
            >
              <Ionicons
                name={mobileMenuOpen ? "close-outline" : "menu-outline"}
                size={28}
                color={navActive}
                data-oid="v:hptdo"
              />
            </TouchableOpacity>

            {mobileMenuOpen && (
              <View
                style={[
                  styles.mobileMenu,
                  { backgroundColor: solidBgColor, borderColor: solidBorderColor },
                ]}
                data-oid="0i1f1.g"
              >
                <TouchableOpacity
                  style={[
                    styles.mobileMenuItem,
                    isRouteActive("Home") && [styles.activeMobileMenuItem, { backgroundColor: theme === "dark" ? "#34343B" : "#F0F0F0" }],
                  ]}
                  onPress={navigateToHome}
                  data-oid="pnrkljo"
                >
                  <Ionicons
                    name="home-outline"
                    size={20}
                    color={isRouteActive("Home") ? navActive : navText}
                    data-oid="stm:b.u"
                  />

                  <Text
                    style={[
                      styles.mobileMenuText,
                      isRouteActive("Home") && styles.activeMobileMenuText,
                      { color: isRouteActive("Home") ? navActive : navText },
                    ]}
                    data-oid="09j_y20"
                    {...navLinkProps("/", navigateToHome)}
                  >
                    Home
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.mobileMenuItem,
                    isRouteActive("About") && [styles.activeMobileMenuItem, { backgroundColor: theme === "dark" ? "#34343B" : "#F0F0F0" }],
                  ]}
                  onPress={navigateToAbout}
                  data-oid="0kd84c:"
                >
                  <Ionicons
                    name="information-circle-outline"
                    size={20}
                    color={isRouteActive("About") ? navActive : navText}
                    data-oid="k0de7qf"
                  />

                  <Text
                    style={[
                      styles.mobileMenuText,
                      isRouteActive("About") && styles.activeMobileMenuText,
                      { color: isRouteActive("About") ? navActive : navText },
                    ]}
                    data-oid="4saki.o"
                    {...navLinkProps("/about", navigateToAbout)}
                  >
                    About
                  </Text>
                </TouchableOpacity>
                {/* Team page temporarily hidden
                <TouchableOpacity
                  style={[
                    styles.mobileMenuItem,
                    isRouteActive("Team") && [styles.activeMobileMenuItem, { backgroundColor: theme === "dark" ? "#34343B" : "#F0F0F0" }],
                  ]}
                  onPress={navigateToTeam}
                >
                  <Ionicons
                    name="people-outline"
                    size={20}
                    color={isRouteActive("Team") ? navActive : navText}
                  />

                  <Text
                    style={[
                      styles.mobileMenuText,
                      isRouteActive("Team") && styles.activeMobileMenuText,
                      { color: isRouteActive("Team") ? navActive : navText },
                    ]}
                  >
                    Team
                  </Text>
                </TouchableOpacity>
                */}
                <TouchableOpacity
                  style={[
                    styles.mobileMenuItem,
                    isRouteActive("Contact") && [styles.activeMobileMenuItem, { backgroundColor: theme === "dark" ? "#34343B" : "#F0F0F0" }],
                  ]}
                  onPress={navigateToContact}
                >
                  <Ionicons
                    name="mail-outline"
                    size={20}
                    color={isRouteActive("Contact") ? navActive : navText}
                  />

                  <Text
                    style={[
                      styles.mobileMenuText,
                      isRouteActive("Contact") && styles.activeMobileMenuText,
                      { color: isRouteActive("Contact") ? navActive : navText },
                    ]}
                    {...navLinkProps("/contact", navigateToContact)}
                  >
                    Contact
                  </Text>
                </TouchableOpacity>

                {isSchoolAdmin && (
                  <TouchableOpacity
                    style={[
                      styles.mobileMenuItem,
                      isRouteActive("SchoolAdminDashboard") && [styles.activeMobileMenuItem, { backgroundColor: theme === "dark" ? "#34343B" : "#F0F0F0" }],
                    ]}
                    onPress={navigateToSchoolAdminDashboard}
                  >
                    <Ionicons
                      name="school-outline"
                      size={20}
                      color={isRouteActive("SchoolAdminDashboard") ? navActive : navText}
                    />

                    <Text
                      style={[
                        styles.mobileMenuText,
                        isRouteActive("SchoolAdminDashboard") && styles.activeMobileMenuText,
                        { color: isRouteActive("SchoolAdminDashboard") ? navActive : navText },
                      ]}
                    >
                      School Dashboard
                    </Text>
                  </TouchableOpacity>
                )}

                {isTeacher && (
                  <TouchableOpacity
                    style={[
                      styles.mobileMenuItem,
                      isRouteActive("TeacherPortal") && [styles.activeMobileMenuItem, { backgroundColor: theme === "dark" ? "#34343B" : "#F0F0F0" }],
                    ]}
                    onPress={navigateToTeacherPortal}
                  >
                    <Ionicons
                      name="person-outline"
                      size={20}
                      color={isRouteActive("TeacherPortal") ? navActive : navText}
                    />

                    <Text
                      style={[
                        styles.mobileMenuText,
                        isRouteActive("TeacherPortal") && styles.activeMobileMenuText,
                        { color: isRouteActive("TeacherPortal") ? navActive : navText },
                      ]}
                    >
                      Teacher Portal
                    </Text>
                  </TouchableOpacity>
                )}

                {isAdmin && onAdminPress && (
                  <TouchableOpacity
                    style={[
                      styles.mobileMenuItem,
                      { backgroundColor: "transparent" }
                    ]}
                    onPress={onAdminPress}
                    data-oid="q8j2__4"
                  >
                    <Ionicons
                      name="settings-outline"
                      size={20}
                      color={navText}
                      data-oid="ijgbym3"
                    />

                    <Text
                      style={[styles.mobileMenuText, { color: navText }]}
                      data-oid="se90hl1"
                    >
                      Admin
                    </Text>
                  </TouchableOpacity>
                )}

                <View style={[styles.mobileMenuActions, { borderTopColor: solidBorderColor }]} data-oid="5-rtvns">
                  <AuthButton data-oid="6jd7l4z" />
                  <TouchableOpacity
                    style={[styles.iconButton, { backgroundColor: theme === "dark" ? "#34343B" : "#F4F4F5" }]}
                    activeOpacity={0.7}
                    onPress={toggleTheme}
                    data-oid="xqq7ht:"
                  >
                    <Animated.View
                      style={{
                        transform: [
                          {
                            rotate: themeIconRotation.interpolate({
                              inputRange: [0, 1],
                              outputRange: ["0deg", "360deg"],
                            }),
                          },
                        ],
                      }}
                      data-oid="d47hs7j"
                    >
                      <Ionicons
                        name={theme === "dark" ? "sunny" : "moon"}
                        size={20}
                        color={iconColor}
                        data-oid="4.ny708"
                      />
                    </Animated.View>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </>
        ) : (
          <View style={styles.navContainer} data-oid="moei4lh">
            <TouchableOpacity
              style={[
                styles.navItem,
                isRouteActive("Home") && styles.activeNavItem,
                { borderBottomColor: navActive },
              ]}
              onPress={navigateToHome}
              data-oid="t7lgamn"
            >
              <Text
                style={[
                  styles.navText,
                  isRouteActive("Home") && styles.activeNavText,
                  { color: isRouteActive("Home") ? navActive : navText },
                ]}
                data-oid="ziludoy"
                {...navLinkProps("/", navigateToHome)}
              >
                Home
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.navItem,
                isRouteActive("About") && styles.activeNavItem,
                { borderBottomColor: navActive },
              ]}
              onPress={navigateToAbout}
              data-oid="p90pe28"
            >
              <Text
                style={[
                  styles.navText,
                  isRouteActive("About") && styles.activeNavText,
                  { color: isRouteActive("About") ? navActive : navText },
                ]}
                data-oid="6m7p0i4"
                {...navLinkProps("/about", navigateToAbout)}
              >
                About
              </Text>
            </TouchableOpacity>
            {/* Team page temporarily hidden
            <TouchableOpacity
              style={[
                styles.navItem,
                isRouteActive("Team") && styles.activeNavItem,
                { borderBottomColor: navActive },
              ]}
              onPress={navigateToTeam}
            >
              <Text
                style={[
                  styles.navText,
                  isRouteActive("Team") && styles.activeNavText,
                  { color: isRouteActive("Team") ? navActive : navText },
                ]}
              >
                Team
              </Text>
            </TouchableOpacity>
            */}
            <TouchableOpacity
              style={[
                styles.navItem,
                isRouteActive("Contact") && styles.activeNavItem,
                { borderBottomColor: navActive },
              ]}
              onPress={navigateToContact}
            >
              <Text
                style={[
                  styles.navText,
                  isRouteActive("Contact") && styles.activeNavText,
                  { color: isRouteActive("Contact") ? navActive : navText },
                ]}
                {...navLinkProps("/contact", navigateToContact)}
              >
                Contact
              </Text>
            </TouchableOpacity>

            {isSchoolAdmin && (
              <TouchableOpacity
                style={[
                  styles.navItem,
                  isRouteActive("SchoolAdminDashboard") && styles.activeNavItem,
                  { borderBottomColor: navActive },
                ]}
                onPress={navigateToSchoolAdminDashboard}
              >
                <Text
                  style={[
                    styles.navText,
                    isRouteActive("SchoolAdminDashboard") && styles.activeNavText,
                    { color: isRouteActive("SchoolAdminDashboard") ? navActive : navText },
                  ]}
                >
                  School Dashboard
                </Text>
              </TouchableOpacity>
            )}

            {isTeacher && (
              <TouchableOpacity
                style={[
                  styles.navItem,
                  isRouteActive("TeacherPortal") && styles.activeNavItem,
                  { borderBottomColor: navActive },
                ]}
                onPress={navigateToTeacherPortal}
              >
                <Text
                  style={[
                    styles.navText,
                    isRouteActive("TeacherPortal") && styles.activeNavText,
                    { color: isRouteActive("TeacherPortal") ? navActive : navText },
                  ]}
                >
                  Teacher Portal
                </Text>
              </TouchableOpacity>
            )}

            <View style={styles.actionsContainer} data-oid="3nq4kva">
              <TouchableOpacity
                style={styles.iconButton}
                activeOpacity={0.7}
                onPress={toggleTheme}
                data-oid="h9str-b"
              >
                <Animated.View
                  style={{
                    transform: [
                      {
                        rotate: themeIconRotation.interpolate({
                          inputRange: [0, 1],
                          outputRange: ["0deg", "360deg"],
                        }),
                      },
                    ],
                  }}
                  data-oid="8.rd_wj"
                >
                  <Ionicons
                    name={theme === "dark" ? "sunny" : "moon"}
                    size={20}
                    color={iconColor}
                    data-oid="kusjay4"
                  />
                </Animated.View>
              </TouchableOpacity>

              {isAdmin && onAdminPress && (
                <TouchableOpacity
                  style={styles.iconButton}
                  activeOpacity={0.7}
                  onPress={onAdminPress}
                  data-oid="--:vi0o"
                >
                  <Ionicons
                    name="settings-outline"
                    size={20}
                    color={navActive}
                    data-oid="vzbd_e8"
                  />
                </TouchableOpacity>
              )}

              <AuthButton data-oid="4mmp2uw" />
            </View>
          </View>
        )}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    width: "100%",
    paddingTop: Platform.OS === "ios" ? 50 : 40,
    zIndex: 100,
    borderBottomWidth: 1,
  },
  scrolledContainer: {
    shadowOpacity: 0.1,
    elevation: 4,
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 16,
    paddingHorizontal: 24,
    maxWidth: 1200,
    alignSelf: "center",
    width: "100%",
  },
  logoContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  logoTouchable: {
    flexDirection: "row",
    alignItems: "center",
  },
  logoIconContainer: {
    backgroundColor: "#F5F5F5",
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  logoText: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#272727",
    fontFamily: "Inter-Bold",
    letterSpacing: -0.5,
  },
  opportunities: {
    fontSize: 12,
    color: "#555555",
    textTransform: "uppercase",
    letterSpacing: 1,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  navContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  navItem: {
    marginHorizontal: 12,
    paddingVertical: 8,
    paddingHorizontal: 2,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  activeNavItem: {},
  navText: {
    color: "#555555",
    fontSize: 15,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  activeNavText: {
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  actionsContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 20,
  },
  iconButton: {
    marginHorizontal: 8,
    padding: 8,
    borderRadius: 8,
  },
  mobileMenuButton: {
    padding: 8,
    marginLeft: 12,
  },
  mobileMenu: {
    position: "absolute",
    top: Platform.OS === "ios" ? 80 : 60,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 200,
  },
  mobileMenuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginVertical: 2,
  },
  activeMobileMenuItem: {
    backgroundColor: "#f0f0f0",
  },
  mobileMenuText: {
    fontSize: 16,
    color: "#272727",
    marginLeft: 12,
    fontFamily: "Inter-Medium",
    fontWeight: "500",
  },
  activeMobileMenuText: {
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  mobileMenuActions: {
    marginTop: 16,
    paddingTop: 16,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopWidth: 1,
    borderTopColor: "#E4E4E7",
  },
  canadaFlagContainer: {
    marginLeft: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  canadaFlag: {
    width: 40,
    height: 30,
  },

});

export default Header;
